/**
 * AtlasIntelligenceBadge — the compact Intelligence Index pill that lives
 * in the Atlas header.
 *
 * D4 residual (11-Jul-2026):
 *   • Expandable breakdown drawer DELETED per operator directive.
 *     The pill is now display-only — it shows the composite score
 *     without a click-to-expand affordance.
 *   • Backend endpoint /api/atlas/intelligence stays intact (unchanged
 *     contract); only the visible drawer surface is removed.
 *   • Hover title shortened to "Intelligence Index" — the previous
 *     "click to see the eight sub-signals" wording is no longer accurate
 *     with the drawer gone. Awaiting operator's approved hover copy
 *     (with Canadian "artefacts") before finalising.
 *
 * Contract with backend: GET /api/atlas/intelligence?topic=<partition>
 *   → { topic, composite_score, computed_at, signals: [...], weights_version }
 * Only `composite_score` is rendered here now.
 */
import React, { useCallback, useEffect, useState } from 'react';
import authedAxios from '../../lib/authedAxios';
import { Activity, Loader2 } from 'lucide-react';
import HoverTip from '../HoverTip';

const API_ROOT = (
  process.env.REACT_APP_BACKEND_URL || ''
).replace(/\/$/, '') + '/api';

const scoreTone = (score) => {
  if (score >= 70) return {
    ring: 'border-emerald-400/60',
    text: 'text-emerald-200',
    glow: '0 0 12px rgba(52, 211, 153, 0.45)',
  };
  if (score >= 40) return {
    ring: 'border-amber-400/60',
    text: 'text-amber-200',
    glow: '0 0 12px rgba(251, 191, 36, 0.45)',
  };
  return {
    ring: 'border-rose-400/60',
    text: 'text-rose-200',
    glow: '0 0 12px rgba(251, 113, 133, 0.45)',
  };
};

export const AtlasIntelligenceBadge = ({ topic }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (t) => {
    if (!t) {
      setData(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await authedAxios.get(
        `${API_ROOT}/atlas/intelligence`,
        { params: { topic: t }, timeout: 15000 },
      );
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(topic); }, [topic, load]);

  if (!topic) return null;

  const score = data?.composite_score ?? 0;
  const tone = scoreTone(score);

  return (
    <div className="relative group">
      <div
        data-testid="atlas-intelligence-badge"
        className={
          'flex items-center gap-2 rounded-full border bg-slate-950/70 px-3 py-1.5 ' +
          'text-[12px] font-mono uppercase tracking-widest ' +
          tone.ring + ' ' + tone.text
        }
        style={{ boxShadow: tone.glow }}
      >
        <Activity className="h-3.5 w-3.5" />
        <span className="text-[10px] opacity-70">Intelligence</span>
        {loading ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : error ? (
          <span data-testid="atlas-intelligence-error" className="text-rose-300">
            offline
          </span>
        ) : (
          <span
            data-testid="atlas-intelligence-score"
            className="text-sm font-bold tabular-nums"
          >
            {score}
          </span>
        )}
        {!loading && !error && (
          <span className="text-[10px] opacity-50">/100</span>
        )}
      </div>
      {/* D7 · 2026-02-14 — converted from native `title` attribute to
          the shared HoverTip so the copy inherits the 20 px / 18 px
          governance rule (was rendering at native OS default ~11-12 px). */}
      <HoverTip
        testId="atlas-intelligence-hover-tag"
        align="bottom-right"
        wide
        title="Intelligence Score"
        description="The Intelligence Score is a combination of several attributes. This includes aspects like recency, the quality of content, the connections between artefacts and several other factors. The more connected items or documents you add to a Topic the smarter the AI Agent will become and the higher the score."
      />
    </div>
  );
};

export default AtlasIntelligenceBadge;
